import { Alert, Button, Divider, Form, message, Modal, Select, SelectProps, Space, Tag } from "antd";
import { useEffect, useState } from "react";
import { useForm } from "antd/lib/form/Form";
import { useAuth } from "../../Contexts/AuthContext";
import { BookyInput, InputType } from "../Forms/BookyInput";
import { GroupService } from "../../Services/GroupService";
import { TextService } from "../../Services/TextService";
import { MemberService } from "../../Services/MemberService";
import { WebApiCompanyDtoCompanyDto, WebApiMemberDtoMemberDto, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto, WebApiServicesDataTransferObjectsGroupServiceGroupResultDto } from "../../Repository/eventbookyapi";
import { BookyButton } from "../Buttons/BookyButton";
import { MemberAutocomplete } from "../AutoComplete/MemberAutocomplete";
import { MemberTable } from "../Table/MemberTable";
import { MemberPaymentStatus } from "../../Types/MemberPaymentStatus";
import { BookySelectPaymentStatus } from "../Forms/BookyPaymentStatusSelect";
import { EmailFormType } from "../../Types/EmailFormType";
import { CompanyService } from "../../Services/CompanyService";
import { ContactAutoComplete } from "../AutoComplete/ContactAutocomplete";
import { EmailTemplateService } from "../../Services/EmailTemplateService";
import { PaymentConfiguration } from "../../Types/PaymentConfiguration";
import { BookyCheckbox } from "../Forms/BookyCheckbox";
import { EmailNotificationPreference } from "../../Types/EmailNotificationPreference";
import { CheckCircleFilled, CheckCircleOutlined } from "@ant-design/icons";
import { FilterMemberType } from "../../Types/FilterMemberType";
import { AutoCompleteType } from "../../Types/AutoCompleteType";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { MemberFieldService } from "../../Services/MemberFieldService";
import { FilterOperator } from "../../Types/FilterOperator";
import { useNavigate } from "react-router-dom";
import { FamilyMemberConfiguration } from "../../Types/FamilyMemberConfiguration";
import { filterByRecipientType, RecipientTypeFilter } from "../../Helpers/FamilyMemberHelper";

export const SmsForm = (props: any) => {
    const navigate = useNavigate();
    const [memberFieldSettings, setMemberFieldSettings] = useState<any[]>([]);
    const [form] = useForm();
    const [smsMessage, setSmsMessage] = useState("" as any);
    const [externalPhoneNumbers, setExternalPhoneNumbers] = useState([] as any[]);
    const [smsIsValid, setSmsIsValid] = useState(false as boolean);
    const [company, setCompany] = useState({} as WebApiCompanyDtoCompanyDto);
    const [loading, setLoading] = useState(false as boolean);
    const [showConfirm, setShowConfirm] = useState(false as boolean);
    const [selectedGroup, setSelectedGroup] = useState([] as number[]);
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(undefined as MemberPaymentStatus | undefined);
    const [isAllowedToSendGroupMails, setIsAllowedToSendGroupMails] = useState(true as boolean);
    const { user } = useAuth();
    const [selectedMembers, setSelectedMembers] = useState([] as WebApiMemberDtoMemberDto[]);
    const [recipientType, setRecipientType] = useState<RecipientTypeFilter>("all");
    const invalidPhoneCount = selectedMembers.filter(m => m.phoneNumber?.startsWith('0')).length;

    const GetText = TextService.GetText;
    const familyMembersEnabled = user?.familyMemberConfiguration === FamilyMemberConfiguration.Enabled;

    useEffect(() => {
        MemberFieldService.GetMemberFields().then(setMemberFieldSettings);

        if (props.groups != null && props.groups.length > 0) {
            setSelectedGroup(props.groups[0]);
            form.setFieldValue("group", props.groups[0].id)
        }

        CompanyService.GetCompany().then(company => {
            setCompany(company);
        })

        GroupService.IsAllowedToSendGroupMails().then(result => {
            setIsAllowedToSendGroupMails(result.toString() === true.toString());
        });
    }, [props]);

    const onReminderFinish = async (formValues: any) => {

        if (hasNoMembersOrEmails()) {
            message.warning(GetText("SmsForm_NoRecipientsSelected"));
            return;
        }

        if (stringIsEmpty(smsMessage)) {
            message.warning(GetText("SmsForm_MessageIsRequired"));
            return;
        }

        setShowConfirm(true);
    };

    const onFilterMembers = async () => {
        let filteredMembers: WebApiMemberDtoMemberDto[] = [];
        const sendToMembersWithoutEmail = form.getFieldValue("sendToMembersWithoutEmail") === true;
        const filters = [{ fieldName: "PhoneNumber", operator: FilterOperator.IsNotEmpty }];

        if (sendToMembersWithoutEmail) {
            filters.push({ fieldName: "Email", operator: FilterOperator.IsEmpty });
            filters.push({ fieldName: "Email2", operator: FilterOperator.IsEmpty });
        }

        if (!selectedGroup.length) {
            message.warning(GetText("EmailForm_SelectOneGroup"));
        }

        filteredMembers = await MemberService.GetFilteredMembers({
            status: selectedPaymentStatus,
            emailNotificationPreference: form.getFieldValue("emailNotificationPreference") == 0 || !form.getFieldValue("emailNotificationPreference") ? EmailNotificationPreference.Unspecified : EmailNotificationPreference.NoInformationEmails,
            isAllMembers: selectedGroup.length == 0 || selectedGroup.some(p => p == -1) ? true : false,
            groupsIds: selectedGroup,
            filters: filters
        });

        if (familyMembersEnabled) {
            filteredMembers = filterByRecipientType(filteredMembers, recipientType);
        }

        setSelectedMembers(filteredMembers);

        if (filteredMembers.length == 0) {
            message.warning(GetText("SmsForm_NoMembersFound"))
        }
    }


    const onSendConfirmOK = async () => {
        setLoading(true);

        if (selectedMembers.length == 0) {
            message.error(GetText("SmsForm_SelectAtLeastOneMember"));
            setLoading(false);
            setShowConfirm(false);
            return;
        }

        if (selectedMembers.length > user?.smsCredits!) {
            message.error(GetText("SmsForm_NotEnoughCredits"));
            setLoading(false);
            setShowConfirm(false);
            return;
        }

        var memberIds: number[] = selectedMembers.map(m => m.memberId!);

        try {
            const jobId = await MemberService.SendSmsToMembers({
                text: smsMessage,
                memberIds: memberIds
            });
            setLoading(false);
            form.setFieldValue("message", "");
            setShowConfirm(false);
            // Navigate to batch job status page
            navigate(`/${getCurrentLanguage()}/batchJobStatus/${jobId}`);
        } catch {
            message.error(GetText("SmsForm_SmsIsSentFailed"));
            setLoading(false);
            setShowConfirm(false);
        }
    };

    const onSendConfirmOKExternal = async () => {
        setLoading(true);

        if (externalPhoneNumbers.length == 0) {
            message.error(GetText("SmsForm_SelectAtLeastOnePhoneNumber"));
            setLoading(false);
            setShowConfirm(false);
            return;
        }

        if (externalPhoneNumbers.length > user?.smsCredits!) {
            message.error(GetText("SmsForm_NotEnoughCredits"));
            setLoading(false);
            setShowConfirm(false);
            return;
        }

        if (company.smsSender == null || company.smsSender == "") {
            message.error(GetText("SmsForm_MissingSender"));
            setLoading(false);
            setShowConfirm(false);
            return;
        }


        try {
            const jobId = await EmailTemplateService.SendSmsToExternal({
                text: smsMessage,
                phoneNumbers: externalPhoneNumbers
            });
            setLoading(false);
            form.setFieldValue("message", "");
            setShowConfirm(false);
            // Navigate to batch job status page
            navigate(`/${getCurrentLanguage()}/batchJobStatus/${jobId}`);
        } catch {
            message.error(GetText("SmsForm_SmsIsSentFailed"));
            setLoading(false);
            setShowConfirm(false);
        }
        setShowConfirm(false);
    }

    const onAutoCompleteSelect = (member: WebApiMemberDtoMemberDto) => {
        let newList: WebApiMemberDtoMemberDto[] = selectedMembers.map(p => p);
        if (!selectedMembers.some((e: WebApiMemberDtoMemberDto) => e.memberId === member.memberId)) {
            newList.push(member);
        }
        setSelectedMembers(newList);
    }

    const removeMember = (memberToRemove: WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto) => {
        let newList = selectedMembers.filter(p => p.memberId != memberToRemove.memberId);
        setSelectedMembers(newList);
    };


    const groupOptions = () => {
        const options: SelectProps['options'] = [];

        props.groups.forEach((element: WebApiServicesDataTransferObjectsGroupServiceGroupResultDto) => {
            options.push({
                label: element.name,
                value: element.id,
            });
        });

        return options;
    }

    const handleGroupChange = async (value: number[]) => {
        setSelectedGroup(value);
    };

    const onSendConfirmCancel = async () => {
        setShowConfirm(false);
    };

    const onSelectPaymentChanged = async (value: MemberPaymentStatus) => {
        setSelectedPaymentStatus(value);
    };


    const onMessageChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const textContainsPhoneNumbers = stringIsEmpty(e.target.value);
        setSmsIsValid(!textContainsPhoneNumbers);
        console.log(e.target.value);
        form.setFieldValue("message", e.target.value);
        setSmsMessage(e.target.value);
    };

    const onEmailsUpdated = async (onEmailsUpdated: any) => {
        setExternalPhoneNumbers(onEmailsUpdated => ([
            ...onEmailsUpdated,
        ]));
    }

    const hasNoMembersOrEmails = () => {
        return (selectedMembers.length == 0 && externalPhoneNumbers.length == 0)
    }

    const stringIsEmpty = (subject: string) => {
        return subject === "";
    }

    return <>
        <Modal
            title={GetText("SmsForm_ConfirmText")}
            open={showConfirm}
            onOk={props.type === EmailFormType.EmailFormByExternal ? onSendConfirmOKExternal : onSendConfirmOK}
            confirmLoading={loading}
            onCancel={onSendConfirmCancel}
        >
            {props.type === EmailFormType.EmailFormByExternal ?
                (GetText("SmsForm_ConfirmText1") + externalPhoneNumbers.length + GetText("SmsForm_ConfirmText2")) :
                (GetText("SmsForm_ConfirmText1") + selectedMembers.length + GetText("SmsForm_ConfirmText2"))}
        </Modal>
        {user?.hasPaidSubscription ?
            <>
                {isAllowedToSendGroupMails === true ? <Form
                    form={form}
                    layout="vertical"
                    onFinish={onReminderFinish}
                    autoComplete="off"
                >
                    <Form.Item
                    >
                        {company.smsSender ? <>{GetText("SmsForm_From")} {company.smsSender}</> : <i>{GetText("SmsForm_SetupSenderInfo")}</i>}
                    </Form.Item>
                    <div style={{
                        backgroundColor: '#fafafa',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #e8e8e8',
                        marginBottom: '30px'
                    }}>
                        <h3>{!hasNoMembersOrEmails() ? <CheckCircleFilled style={{ color: "green" }} /> : <CheckCircleOutlined style={{ color: "grey" }} />} {GetText("EmailForm_Step1")}</h3>
                        <Divider style={{ marginTop: "5px" }} />
                        <span style={{ color: "red" }}>*</span> {GetText("SmsForm_ChooseReceiver")}
                        {props.type === EmailFormType.EmailFormByGroup && props.groups.length > 0 ? <><br />
                            <Select placeholder={GetText("EmailForm_ChooseCategories")} onChange={handleGroupChange} style={{ width: '400px', marginBottom: "10px" }} mode="multiple" options={groupOptions()}></Select>
                            <br /> {props.user?.paymentConfiguration == PaymentConfiguration.YearlyPayments ? <BookySelectPaymentStatus style={{ width: "400px" }} onSelectPaymentChanged={onSelectPaymentChanged} /> : ""}
                            {familyMembersEnabled ? <div style={{ margin: "10px 0" }}>
                                <span style={{ fontSize: 12, color: '#8c8c8c' }}>{GetText("Communication_RecipientTypeLabel")}</span>
                                <Select
                                    value={recipientType}
                                    style={{ width: "400px", display: 'block' }}
                                    onChange={(value: RecipientTypeFilter) => setRecipientType(value)}
                                    options={[
                                        { label: GetText("Communication_RecipientAll"), value: "all" },
                                        { label: GetText("Communication_RecipientPrimariesOnly"), value: "primaries" },
                                        { label: GetText("Communication_RecipientFamilyOnly"), value: "family" },
                                    ]}
                                />
                            </div> : ""}
                            <br /><BookyCheckbox noMarginBottom alignLabelRight={true} label={GetText("EmailForm_OverrideEmailSetting")} name="emailNotificationPreference"></BookyCheckbox>
                            <BookyCheckbox alignLabelRight label={GetText("SmsForm_FilterMissingEmailAddress")} name="sendToMembersWithoutEmail"></BookyCheckbox>
                        </> : ""}

                        {props.type === EmailFormType.EmailFormByMember ? <MemberAutocomplete AutoCompleteType={AutoCompleteType.PhoneNumber} showList={false} onAutoCompleteSelect={onAutoCompleteSelect} listHeader={GetText("ReminderForm_ReminderListHeader")} members={props.memberResult.members} /> : ""}
                        {props.type === EmailFormType.EmailFormByGroup ? <><br /><BookyButton style={{ marginTop: "10px" }} text={GetText("EmailForm_CreateSendList")} onClick={onFilterMembers}></BookyButton></> : ""}
                        {props.type === EmailFormType.EmailFormByExternal ? <><br /> <ContactAutoComplete AutoCompleteType={AutoCompleteType.PhoneNumber} descriptionText={GetText("AutoComplete_HelpTextAddPhone")} showList listHeader={GetText("SmsForm_ExternalPhoneHeader")} emails={externalPhoneNumbers} onEmailsUpdated={onEmailsUpdated} /></> : ""}

                        {selectedMembers.length === 0 ? "" : <MemberTable user={user} isMail={true} members={selectedMembers} style={{ minWidth: '400px', marginTop: "10px" }} removeMember={removeMember} memberFieldSettings={memberFieldSettings} />}
                        {selectedMembers.length > 0 ? <><br /><i>{selectedMembers.length} {GetText("EmailForm_ChosenReceivers")}</i></> : ""}
                        {invalidPhoneCount > 0 && (
                            <Alert
                                style={{ marginTop: 12 }}
                                type="warning"
                                showIcon
                                message={GetText("SmsForm_InvalidRecipientPhoneTitle")}
                                description={`${invalidPhoneCount} ${GetText("SmsForm_InvalidRecipientPhoneWarning")}`}
                            />
                        )}
                    </div>

                    <div style={{
                        backgroundColor: '#fafafa',
                        padding: '20px',
                        borderRadius: '8px',
                        border: '1px solid #e8e8e8'
                    }}>
                        <h3 style={{ marginTop: "0px" }}>{smsIsValid ? <CheckCircleFilled style={{ color: "green" }} /> : <CheckCircleOutlined style={{ color: "grey" }} />}  {GetText("EmailForm_Step2")}</h3>
                        <Divider style={{ margin: "5px" }} />
                        <BookyInput onChange={onMessageChange} showCount
                            maxLength={160} type={InputType.multiline} label={GetText("EmailForm_Message")} name="message" style={{ width: '400px', marginTop: "10px" }} requiredMessage={GetText("SmsForm_MessageIsRequired")} ></BookyInput>

                        <Form.Item>
                            <Space style={{ width: '100%', justifyContent: 'left', margin: 0 }}>
                                <Button type="primary" htmlType="submit" disabled={!company.smsSender}>
                                    {GetText("SmsForm_SendSmsButtonText")}
                                </Button>
                            </Space>
                        </Form.Item>
                    </div>

                </Form> : <Alert
                    message={GetText("EmailForm_AlertSuspendedMessage")}
                    description={GetText("EmailForm_AlertSuspendedDescription")}
                    type="warning"
                />}
            </> : <span><Tag color="#1890ff">{GetText("Common_Subscription")}</Tag><a href={"/" + getCurrentLanguage() + "/Pricing"}>{GetText("Common_UpgradeToPremium")}</a>{GetText("EmailForm_PremiumSendingMessageToGroups")}</span>
        }

    </>
}